import React from 'react';
import { Button } from "../ui/button";
import { ChevronDown, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const gapCards = [
    {
        pain: 'Sos Data Analyst o BI, sabés SQL y Power BI, pero sentís que tu techo llegó.',
        desire: 'Data Engineer con Databricks, Spark y trabajo remoto en USD.',
        painLabel: 'HOY',
        desireLabel: 'DESPUÉS',
    },
    {
        pain: 'Venís de otra profesión y sabés que datos paga bien, pero no sabés por dónde arrancar.',
        desire: 'Transición real en meses, con alguien que ya hizo el camino.',
        painLabel: 'HOY',
        desireLabel: 'DESPUÉS',
    },
    {
        pain: 'Estudiás por tu cuenta, mandás CVs, y nadie te llama.',
        desire: 'Proyecto real, herramientas que el mercado pide, y entrevistas que pasás.',
        painLabel: 'HOY',
        desireLabel: 'DESPUÉS',
    },
];

interface HeroProps {
    onIniciaRedirect?: () => void;
    onScrollToSection: (sectionId: string) => void;
    darkBg?: boolean;
}

export default function Hero({ onIniciaRedirect, onScrollToSection, darkBg }: HeroProps) {
    const chevronCls = darkBg ? 'text-green-400 hover:text-green-300' : 'text-slate-500 hover:text-slate-700';

    return (
        <section className="pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto">
                {/* --- ABOVE THE FOLD: Hook → Promise → CTA --- */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 sm:mb-16"
                >
                    <p className="text-sm sm:text-base font-medium text-green-400 tracking-wide uppercase mb-6">
                        Data Engineering — el único rol del que depende la IA
                    </p>

                    <div className="max-w-5xl mx-auto mb-8 rounded-2xl bg-gradient-to-br from-green-900/90 via-emerald-900/80 to-green-950/90 border border-green-400/40 px-8 py-8 sm:px-14 sm:py-10 backdrop-blur-sm shadow-2xl shadow-green-500/10">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4">
                            ¿Estás buscando más libertad, mejores sueldos, y un mejor estilo de vida?
                        </h1>
                    </div>

                    <div className="max-w-xl mx-auto mb-8 rounded-2xl bg-gradient-to-r from-gray-800/80 via-gray-900/90 to-gray-800/80 border border-green-500/30 px-6 py-5 backdrop-blur-sm">
                        <p className="text-lg sm:text-xl text-white font-medium leading-relaxed">
                            Te ayudo con un bootcamp intensivo de 10 semanas
                        </p>
                        <p className="text-sm sm:text-base text-green-300/80 mt-1">
                            La misma transformación que voy a guiarte, es la que viví.
                        </p>
                    </div>

                    <div className="flex flex-col items-center gap-2 mb-4">
                        <Button
                            size="lg"
                            className="w-full sm:w-auto text-white px-8 py-5 sm:px-10 sm:py-6 text-lg sm:text-xl rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 bg-green-600 hover:bg-green-700"
                            onClick={onIniciaRedirect}
                        >
                            <Sparkles className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
                            Contactame y empecemos
                        </Button>
                    </div>

                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm sm:text-base font-medium text-green-400/80 mt-4">
                        <span>+300 alumnos capacitados</span>
                        <span className="text-green-500/50">·</span>
                        <span>+40 profesionales mentoreados</span>
                    </div>
                </motion.div>

                {/* --- BELOW THE FOLD: Gap cards como expansión --- */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-50px" }}
                >
                    <p className="text-center text-xl sm:text-2xl font-semibold tracking-wide text-white mb-6">
                        ¿Te suena alguno?
                    </p>
                    <div className="grid md:grid-cols-3 gap-4 sm:gap-5 px-2">
                        {gapCards.map((card, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                                viewport={{ once: true }}
                                className="rounded-2xl p-6 text-left border bg-black/60 border-green-500/20 shadow-lg flex flex-col"
                            >
                                <div className="flex-1 mb-3">
                                    <span className="inline-block text-[11px] font-bold tracking-widest uppercase px-2.5 py-1 rounded bg-red-500/20 text-red-400 mb-2">
                                        {card.painLabel}
                                    </span>
                                    <p className="text-base leading-relaxed text-gray-300">{card.pain}</p>
                                </div>
                                <div className="flex items-center gap-2 my-3">
                                    <div className="flex-1 h-px bg-green-500/30" />
                                    <ArrowRight className="w-4 h-4 text-green-400" />
                                    <div className="flex-1 h-px bg-green-500/30" />
                                </div>
                                <div>
                                    <span className="inline-block text-[11px] font-bold tracking-widest uppercase px-2.5 py-1 rounded mb-2 bg-green-500/20 text-green-400">
                                        {card.desireLabel}
                                    </span>
                                    <p className="text-base leading-relaxed font-medium text-green-300">{card.desire}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Flecha para scroll */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                    className="text-center mt-10"
                >
                    <button
                        onClick={() => onScrollToSection('sobre-mi')}
                        className={`transition-colors animate-bounce ${chevronCls}`}
                        aria-label="Conocé mi historia"
                    >
                        <ChevronDown className="w-8 h-8 mx-auto" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
